// src/pages/UsersListPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { downloadUsersExcel, getSubmissions } from '../services/api.service';
import Loading from '../components/loading';
import { FaFileExcel, FaTimes } from 'react-icons/fa';
import Helper from '../services/helper.service';

const UsersListPage = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const data = await getSubmissions();
                const uniqueUsers = Array.from(
                    new Map(
                        data.map(sub => [sub.customer.email, {
                            email: sub.customer.email,
                            firstname: sub.customer.firstname,
                            lastname: sub.customer.lastname,
                            phone: sub.customer.phone
                        }])
                    ).values()
                );
                setUsers(uniqueUsers);
                setFilteredUsers(uniqueUsers);

            } catch (err) {
                Helper.toast("Erreur lors du chargement des utilisateurs", "error");

            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const lower = search.toLowerCase().trim();
        const terms = lower.split(/\s+/); // split search into words

        setFilteredUsers(
            users.filter(user => {
                // Combine all fields into a single lowercase string
                const combined = `${user.firstname || ''} ${user.lastname || ''} ${user.email || ''} ${user.phone || ''}`.toLowerCase();

                // Ensure all search terms are found somewhere in the combined string
                return terms.every(term => combined.includes(term));
            })
        );
    }, [search, users]);

    const handleExport = async () => {
        setExporting(true);
        try {
            await downloadUsersExcel();
        } finally {
            setExporting(false);
        }
    };

    return (
        <section className="max-w-4xl mx-auto p-6">
            <div className="flex items-center justify-between gap-4 mb-6">
                <h1 className="text-2xl font-bold">Liste des utilisateurs</h1>

                <button
                    type="button"
                    onClick={handleExport}
                    disabled={exporting}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <FaFileExcel className="w-4 h-4" />
                    {exporting ? 'Export en cours...' : 'Exporter les utilisateurs'}
                </button>
            </div>

            <div className="relative mb-6 w-1/2">
                <input
                    type="text"
                    placeholder="Rechercher par nom, prénom, email ou téléphone"
                    className="mb-6 w-full border border-gray-300 p-2 rounded"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                    <button
                        onClick={() => setSearch('')}
                        className="absolute right-3 top-3 text-gray-500 text-lg"
                    >
                        <FaTimes className="w-4 h-4" />
                    </button>
                )}
            </div>

            {loading ? (
                <Loading />

            ) : filteredUsers.length === 0 ? (
                <p className="text-center text-gray-500 py-12">Aucun utilisateur trouvé.</p>
            ) : (
                <ul className="space-y-4">
                    {filteredUsers.map(user => (
                        <li
                            key={user.email}
                            onClick={() => navigate(`/user/${encodeURIComponent(user.email)}`)}
                            className="p-4 bg-white border rounded cursor-pointer hover:bg-gray-100"
                        >
                            <p className="text-lg font-semibold">{user.firstname} {user.lastname}</p>
                            <p className="text-gray-600 text-sm">{user.email} - {user.phone}</p>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

export default UsersListPage;